import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Bar, Line } from 'react-chartjs-2';
import 'chart.js/auto';

import './OnCallReportComparison.css';
import LoadingComp from '../common/loading.js';
import { ApiError } from '../common/apiError.js';
import { SEC_ApiCall } from '../func.js';
import { getOnCallReportCallTypeOptions } from './OnCallFunc.js';
import OnCallReportDateFilter from './OnCallReportDateFilter.js';

const DEFAULT_CHART_COLOURS = ['#54abcd', '#bdca9f', '#f9b233', '#ff6666', '#c48e38', '#706f6f'];

// Format a Date object into the yyyy-mm-dd value required by date inputs and report APIs.
function formatDateForInput(dateValue) {
    return dateValue.toISOString().split('T')[0];
}

// Use the same academic-year default as the existing OnCall summary reports.
function getDefaultAcademicYearDates() {
    const today = new Date();
    const currentYear = today.getFullYear();
    const septemberFirstThisYear = new Date(currentYear, 8, 1);
    const startDate = today >= septemberFirstThisYear
        ? septemberFirstThisYear
        : new Date(currentYear - 1, 8, 1);

    return {
        startDate: formatDateForInput(startDate),
        endDate: formatDateForInput(today),
    };
}

// Keep the reports landing response stable before deriving comparison scopes from it.
function normaliseReportsResponse(response) {
    const cleanResponse = response && typeof response === 'object' ? response : {};
    return {
        sections: Array.isArray(cleanResponse.sections) ? cleanResponse.sections : [],
        canViewWholeSchoolData: cleanResponse.canViewWholeSchoolData === true,
    };
}

// Support both direct Lambda proxy bodies and already-parsed frontend API responses.
function normaliseReportResponse(response) {
    if (typeof response === 'string') {
        try {
            return JSON.parse(response);
        } catch (error) {
            console.error('Failed to parse comparison report payload string', error);
            return {};
        }
    }

    if (response && typeof response?.body === 'string') {
        try {
            return JSON.parse(response.body);
        } catch (error) {
            console.error('Failed to parse comparison report response body', error);
            return {};
        }
    }

    return response && typeof response === 'object' ? response : {};
}

function parseSelectedItems(rawItemsValue) {
    return String(rawItemsValue || '')
        .split('|')
        .map((itemName) => itemName.trim())
        .filter(Boolean);
}

function getWholeSchoolYearItems(reportIndex, selectedItems = []) {
    const selectedItemSet = new Set(selectedItems);

    return reportIndex.sections
        .filter((section) => section?.showWholeGroup === true)
        .map((section) => String(section?.title || '').trim())
        .filter((sectionName) => sectionName && (selectedItemSet.size === 0 || selectedItemSet.has(sectionName)))
        .map((sectionName) => ({
            label: sectionName,
            type: 'group',
            groupName: sectionName,
        }));
}

function getClassItems(reportIndex, reportType, groupName, selectedItems = []) {
    const selectedItemSet = new Set(selectedItems);
    const sourceSections = reportType === 'wholeSchool'
        ? reportIndex.sections
        : reportIndex.sections.filter((section) => (
            String(section?.title || '').trim() === String(groupName || '').trim()
        ));

    return sourceSections
        .flatMap((section) => (Array.isArray(section?.classes) ? section.classes : []))
        .map((className) => String(className || '').trim())
        .filter((className) => className && (selectedItemSet.size === 0 || selectedItemSet.has(className)))
        .map((className) => ({
            label: className,
            type: 'class',
            groupName: className,
        }));
}

// Convert the selected comparison mode into the specific group/class reports to fetch.
function buildComparisonItems(reportIndex, reportType, groupName, comparisonMode, selectedItems = []) {
    if (comparisonMode === 'classes') {
        return getClassItems(reportIndex, reportType, groupName, selectedItems);
    }

    if (reportType === 'wholeSchool') {
        return getWholeSchoolYearItems(reportIndex, selectedItems);
    }

    const selectedSection = reportIndex.sections.find((section) => (
        String(section?.title || '').trim() === String(groupName || '').trim()
    ));

    return (Array.isArray(selectedSection?.classes) ? selectedSection.classes : [])
        .map((className) => ({
            label: String(className || '').trim(),
            type: 'class',
            groupName: String(className || '').trim(),
        }))
        .filter((item) => item.label && item.groupName);
}

function buildPupilGroupComparisonResults(sourceReport) {
    const pupilRecords = Array.isArray(sourceReport?.pupilRecords) ? sourceReport.pupilRecords : [];

    return ['MTT', 'ALN', 'FSM'].flatMap((groupKey) => ([
        {
            ...sourceReport,
            label: `Not ${groupKey}`,
            comparisonGroup: groupKey,
            cohort: 'notGroup',
            pupilRecords: pupilRecords.filter((pupilRecord) => !Boolean(pupilRecord?.[groupKey])),
        },
        {
            ...sourceReport,
            label: groupKey,
            comparisonGroup: groupKey,
            cohort: 'inGroup',
            pupilRecords: pupilRecords.filter((pupilRecord) => Boolean(pupilRecord?.[groupKey])),
        },
    ]));
}

function getFullTermRanges(termDateRanges) {
    const terms = termDateRanges?.terms;
    if (!terms || typeof terms !== 'object') {
        return [];
    }

    return Object.values(terms)
        .map((term) => ({
            label: String(term?.name || '').trim(),
            startDate: term?.startDate,
            endDate: term?.endDate,
        }))
        .filter((term) => term.label && term.startDate && term.endDate);
}

function formatWeekStartDate(dateValue) {
    const day = String(dateValue.getDate()).padStart(2, '0');
    const month = String(dateValue.getMonth() + 1).padStart(2, '0');
    const year = dateValue.getFullYear();

    return `${day}/${month}/${year}`;
}

function parseDateInput(dateValue) {
    const [year, month, day] = String(dateValue || '').split('-').map((part) => Number(part));
    if (!year || !month || !day) {
        return new Date();
    }

    return new Date(year, month - 1, day);
}

function getMondayForDate(dateValue) {
    const mondayDate = new Date(dateValue);
    const dayNumber = mondayDate.getDay();
    const daysFromMonday = dayNumber === 0 ? 6 : dayNumber - 1;
    mondayDate.setDate(mondayDate.getDate() - daysFromMonday);
    mondayDate.setHours(0, 0, 0, 0);

    return mondayDate;
}

function buildWeeklyCallCounts(pupilRecords, startDate, endDate) {
    const weeklyCounts = {};
    const currentWeek = getMondayForDate(parseDateInput(startDate));
    const finalWeekStart = getMondayForDate(parseDateInput(endDate));

    while (currentWeek <= finalWeekStart) {
        weeklyCounts[formatWeekStartDate(currentWeek)] = 0;
        currentWeek.setDate(currentWeek.getDate() + 7);
    }

    pupilRecords.forEach((pupilRecord) => {
        (pupilRecord?.onCallRecords || []).forEach((record) => {
            const submissionEpoch = Number(record?.submissionEpoch);
            if (!submissionEpoch) {
                return;
            }

            const weekLabel = formatWeekStartDate(getMondayForDate(new Date(submissionEpoch * 1000)));
            weeklyCounts[weekLabel] = (weeklyCounts[weekLabel] || 0) + 1;
        });
    });

    return weeklyCounts;
}

// Collapse each fetched report payload into the row and chart totals shown on the page.
function buildComparisonRows(comparisonResults, callTypeOptions) {
    return comparisonResults.map((result) => {
        const pupilRecords = Array.isArray(result?.pupilRecords) ? result.pupilRecords : [];
        const allRecords = pupilRecords.flatMap((pupilRecord) => pupilRecord?.onCallRecords || []);
        const callTypeCounts = callTypeOptions.reduce((counts, callType) => ({
            ...counts,
            [callType.typeName]: allRecords.filter((record) => record?.callType === callType.typeName).length,
        }), {});

        return {
            label: result.label,
            comparisonGroup: result.comparisonGroup || '',
            cohort: result.cohort || '',
            totalCalls: allRecords.length,
            pupilsWithCalls: pupilRecords.length,
            callsPerPupil: pupilRecords.length > 0 ? allRecords.length / pupilRecords.length : 0,
            callTypeCounts,
        };
    });
}

export default function OnCallReportComparison() {
    const navigate = useNavigate();
    const params = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const defaultDates = getDefaultAcademicYearDates();
    const reportType = params.type || 'wholeSchool';
    const groupName = params.groupName || '';
    const comparisonMode = searchParams.get('mode') || (reportType === 'wholeSchool' ? 'years' : 'classes');
    const selectedItems = parseSelectedItems(searchParams.get('items'));
    const comparisonParamsKey = `${comparisonMode}|${searchParams.get('items') || ''}`;

    const [isLoadedState, setIsLoadedState] = useState(false);
    const [isApplyingDateFilterState, setIsApplyingDateFilterState] = useState(false);
    const [apiErrorState, setApiErrorState] = useState(false);
    const [comparisonResultsState, setComparisonResultsState] = useState([]);
    const [startDateState, setStartDateState] = useState(searchParams.get('startDate') || defaultDates.startDate);
    const [endDateState, setEndDateState] = useState(searchParams.get('endDate') || defaultDates.endDate);
    const [selectedTermRangeState, setSelectedTermRangeState] = useState(null);

    const callTypeOptions = useMemo(() => {
        const allRecords = comparisonResultsState.flatMap((result) => (
            (result?.pupilRecords || []).flatMap((pupilRecord) => pupilRecord?.onCallRecords || [])
        ));
        const allCallTypes = comparisonResultsState.flatMap((result) => result?.onCallType || []);

        return getOnCallReportCallTypeOptions(allCallTypes, allRecords);
    }, [comparisonResultsState]);

    const comparisonRows = useMemo(() => (
        buildComparisonRows(comparisonResultsState, callTypeOptions)
    ), [comparisonResultsState, callTypeOptions]);

    const termDateRanges = useMemo(() => (
        comparisonResultsState.find((result) => result?.termDateRanges)?.termDateRanges || {}
    ), [comparisonResultsState]);

    const pupilGroupComparisonTables = useMemo(() => {
        if (comparisonMode !== 'pupilGroups') {
            return [];
        }

        return ['MTT', 'ALN', 'FSM'].map((groupKey) => ({
            groupKey,
            notGroup: comparisonRows.find((row) => row.comparisonGroup === groupKey && row.cohort === 'notGroup'),
            inGroup: comparisonRows.find((row) => row.comparisonGroup === groupKey && row.cohort === 'inGroup'),
        }));
    }, [comparisonMode, comparisonRows]);

    const pupilGroupTrendCharts = useMemo(() => {
        if (comparisonMode !== 'pupilGroups') {
            return [];
        }

        return ['MTT', 'ALN', 'FSM'].map((groupKey) => {
            const notGroupResult = comparisonResultsState.find((result) => (
                result.comparisonGroup === groupKey && result.cohort === 'notGroup'
            ));
            const inGroupResult = comparisonResultsState.find((result) => (
                result.comparisonGroup === groupKey && result.cohort === 'inGroup'
            ));
            const notGroupCounts = buildWeeklyCallCounts(notGroupResult?.pupilRecords || [], startDateState, endDateState);
            const inGroupCounts = buildWeeklyCallCounts(inGroupResult?.pupilRecords || [], startDateState, endDateState);
            const weekLabels = Array.from(new Set([
                ...Object.keys(notGroupCounts),
                ...Object.keys(inGroupCounts),
            ])).sort((firstDate, secondDate) => {
                const [firstDay, firstMonth, firstYear] = firstDate.split('/').map(Number);
                const [secondDay, secondMonth, secondYear] = secondDate.split('/').map(Number);
                return new Date(firstYear, firstMonth - 1, firstDay) - new Date(secondYear, secondMonth - 1, secondDay);
            });

            return {
                groupKey,
                data: {
                    labels: weekLabels,
                    datasets: [
                        {
                            label: `Not ${groupKey}`,
                            data: weekLabels.map((weekLabel) => notGroupCounts[weekLabel] || 0),
                            borderColor: '#54abcd',
                            backgroundColor: '#54abcd',
                            tension: 0.35,
                        },
                        {
                            label: groupKey,
                            data: weekLabels.map((weekLabel) => inGroupCounts[weekLabel] || 0),
                            borderColor: '#f9b233',
                            backgroundColor: '#f9b233',
                            tension: 0.35,
                        },
                    ],
                },
            };
        });
    }, [comparisonMode, comparisonResultsState, startDateState, endDateState]);

    const chartData = useMemo(() => ({
        labels: comparisonRows.map((row) => row.label),
        datasets: callTypeOptions.map((callType, index) => ({
            label: callType.typeName,
            data: comparisonRows.map((row) => row.callTypeCounts[callType.typeName] || 0),
            backgroundColor: callType.colourCode || DEFAULT_CHART_COLOURS[index % DEFAULT_CHART_COLOURS.length],
            borderRadius: 6,
        })),
    }), [comparisonRows, callTypeOptions]);

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
            },
        },
        scales: {
            x: {
                stacked: true,
            },
            y: {
                stacked: true,
                beginAtZero: true,
                ticks: {
                    precision: 0,
                },
            },
        },
    };
    const lineChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    precision: 0,
                },
            },
        },
    };

    async function fetchSingleReport(item, startDate, endDate) {
        const response = await SEC_ApiCall('oncall/reports/groupSummary', {
            type: item.type,
            groupName: item.groupName,
            startDate,
            endDate,
        }, setApiErrorState);
        const cleanResponse = normaliseReportResponse(response);

        return {
            ...cleanResponse,
            label: item.label,
            pupilRecords: Array.isArray(cleanResponse?.pupilRecords) ? cleanResponse.pupilRecords : [],
            onCallType: Array.isArray(cleanResponse?.onCallType) ? cleanResponse.onCallType : [],
            termDateRanges: cleanResponse?.termDateRanges || {},
        };
    }

    async function fetchScopeReport(startDate, endDate) {
        if (reportType === 'wholeSchool') {
            const response = await SEC_ApiCall('oncall/reports/wholeSchool', {
                startDate,
                endDate,
            }, setApiErrorState);
            const cleanResponse = normaliseReportResponse(response);

            return {
                ...cleanResponse,
                label: 'Whole School',
                pupilRecords: Array.isArray(cleanResponse?.pupilRecords) ? cleanResponse.pupilRecords : [],
                onCallType: Array.isArray(cleanResponse?.onCallType) ? cleanResponse.onCallType : [],
                termDateRanges: cleanResponse?.termDateRanges || {},
            };
        }

        return fetchSingleReport({
            label: groupName,
            type: 'group',
            groupName,
        }, startDate, endDate);
    }

    function updateReportDateSearchParams(startDate, endDate) {
        const updatedSearchParams = new URLSearchParams(searchParams);
        updatedSearchParams.set('startDate', startDate);
        updatedSearchParams.set('endDate', endDate);
        setSearchParams(updatedSearchParams);
    }

    async function loadComparisonReport(startDate, endDate, isRefresh = false) {
        if (isRefresh) {
            setIsApplyingDateFilterState(true);
        }

        try {
            setApiErrorState(false);
            const reportIndex = normaliseReportsResponse(
                await SEC_ApiCall('oncall/reports', {}, setApiErrorState)
            );

            if (reportType === 'wholeSchool' && !reportIndex.canViewWholeSchoolData) {
                setApiErrorState(true);
                return;
            }

            if (comparisonMode === 'pupilGroups') {
                const sourceReport = await fetchScopeReport(startDate, endDate);
                setComparisonResultsState(buildPupilGroupComparisonResults(sourceReport));
                return;
            }

            if (comparisonMode === 'terms') {
                const sourceReport = await fetchScopeReport(startDate, endDate);
                const fullTermRanges = getFullTermRanges(sourceReport.termDateRanges);
                const termResponses = await Promise.all(fullTermRanges.map(async (termRange) => {
                    const termReport = await fetchScopeReport(termRange.startDate, termRange.endDate);
                    return {
                        ...termReport,
                        label: termRange.label,
                    };
                }));

                setComparisonResultsState(termResponses);
                return;
            }

            const comparisonItems = buildComparisonItems(
                reportIndex,
                reportType,
                groupName,
                comparisonMode,
                selectedItems
            );
            const comparisonResponses = await Promise.all(
                comparisonItems.map((item) => fetchSingleReport(item, startDate, endDate))
            );

            setComparisonResultsState(comparisonResponses);
        } catch (error) {
            console.error('Failed to load OnCall comparison report', error);
            setApiErrorState(true);
        } finally {
            setIsLoadedState(true);
            setIsApplyingDateFilterState(false);
        }
    }

    function handleApplyDateFilter() {
        updateReportDateSearchParams(startDateState, endDateState);
        loadComparisonReport(startDateState, endDateState, true);
    }

    function handleTermRangeChange(selectedRange) {
        setSelectedTermRangeState(selectedRange);

        if (!selectedRange) {
            setStartDateState(defaultDates.startDate);
            setEndDateState(defaultDates.endDate);
            updateReportDateSearchParams(defaultDates.startDate, defaultDates.endDate);
            loadComparisonReport(defaultDates.startDate, defaultDates.endDate, true);
            return;
        }

        setStartDateState(selectedRange.startDate);
        setEndDateState(selectedRange.endDate);
        updateReportDateSearchParams(selectedRange.startDate, selectedRange.endDate);
        loadComparisonReport(selectedRange.startDate, selectedRange.endDate, true);
    }

    useEffect(() => {
        loadComparisonReport(startDateState, endDateState);
    }, [reportType, groupName, comparisonParamsKey]);

    if (!isLoadedState) {
        return <LoadingComp />;
    }

    if (apiErrorState) {
        return <ApiError />;
    }

    const modeTitleMap = {
        pupilGroups: 'MTT/ALN/FSM',
        terms: 'Terms',
        classes: 'Classes',
        years: 'Years',
    };
    const reportTitle = reportType === 'wholeSchool'
        ? 'Whole School Comparison Report'
        : `Comparison Report - Group: ${groupName}`;
    const reportSubtitle = modeTitleMap[comparisonMode] || 'Comparison';
    const totalCallsLabel = comparisonMode === 'pupilGroups'
        ? reportSubtitle
        : `${reportSubtitle} - Total Calls: ${comparisonRows.reduce((total, row) => total + row.totalCalls, 0)}`;

    return (
        <div className='main_div'>
            <div className='internal_main_div'>
                <div className='oncall_header_div'>
                    <h1 className='admin_header'>OnCall Reports</h1>
                </div>

                <div className='oncall_reports_comparison_main_div'>
                    <div className='oncall_reports_comparison_headerRow'>
                        <div className='oncall_reports_comparison_headerText'>
                            <h1 className='oncall_reports_comparison_mainHeader'>{reportTitle}</h1>
                            <h1 className='oncall_reports_comparison_subHeader'>
                                {totalCallsLabel}
                            </h1>
                        </div>
                        <button
                            className='oncall_reports_comparison_backButton'
                            type='button'
                            onClick={() => navigate('/app/oncall/reports')}
                        >
                            Back to Reports
                        </button>
                    </div>

                    <OnCallReportDateFilter
                        startDate={startDateState}
                        endDate={endDateState}
                        setStartDate={setStartDateState}
                        setEndDate={setEndDateState}
                        onApply={handleApplyDateFilter}
                        isLoading={isApplyingDateFilterState}
                        termDateRanges={termDateRanges}
                        selectedTermRange={selectedTermRangeState}
                        onTermRangeChange={handleTermRangeChange}
                    />

                    {comparisonRows.length === 0 ? (
                        <div className='oncall_reports_comparison_emptyState'>
                            No comparison data is available for this report.
                        </div>
                    ) : (
                        <>
                            <div className='oncall_reports_comparison_chartDiv'>
                                <Bar data={chartData} options={chartOptions} />
                            </div>

                            {comparisonMode === 'pupilGroups' ? (
                                <>
                                    <div className='oncall_reports_comparison_pupilGroupTrendGrid'>
                                        {pupilGroupTrendCharts.map((trendChart) => (
                                            <div className='oncall_reports_comparison_pupilGroupTrendCard' key={trendChart.groupKey}>
                                                <h2 className='oncall_reports_comparison_pupilGroupTrendTitle'>{trendChart.groupKey} Weekly Trend</h2>
                                                <div className='oncall_reports_comparison_pupilGroupTrendChart'>
                                                    <Line data={trendChart.data} options={lineChartOptions} />
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className='oncall_reports_comparison_pupilGroupTables'>
                                        {pupilGroupComparisonTables.map(({ groupKey, notGroup, inGroup }) => (
                                            <div className='oncall_reports_comparison_pupilGroupTableWrap' key={groupKey}>
                                                <h2 className='oncall_reports_comparison_pupilGroupTableTitle'>{groupKey}</h2>
                                                <table className='oncall_reports_comparison_pupilGroupTable'>
                                                    <thead>
                                                        <tr>
                                                            <th>Metric</th>
                                                            <th>Non {groupKey}</th>
                                                            <th>{groupKey}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr>
                                                            <td>Total Calls</td>
                                                            <td>{notGroup?.totalCalls || 0}</td>
                                                            <td>{inGroup?.totalCalls || 0}</td>
                                                        </tr>
                                                        <tr>
                                                            <td>Learners With Calls</td>
                                                            <td>{notGroup?.pupilsWithCalls || 0}</td>
                                                            <td>{inGroup?.pupilsWithCalls || 0}</td>
                                                        </tr>
                                                        <tr>
                                                            <td>Calls Per Learner</td>
                                                            <td>{(notGroup?.callsPerPupil || 0).toFixed(1)}</td>
                                                            <td>{(inGroup?.callsPerPupil || 0).toFixed(1)}</td>
                                                        </tr>
                                                        {callTypeOptions.map((callType) => (
                                                            <tr key={`${groupKey}-${callType.typeName}`}>
                                                                <td>{callType.typeName}</td>
                                                                <td>{notGroup?.callTypeCounts?.[callType.typeName] || 0}</td>
                                                                <td>{inGroup?.callTypeCounts?.[callType.typeName] || 0}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <div className='oncall_reports_comparison_tableWrap'>
                                    <table className='oncall_reports_comparison_table'>
                                        <thead>
                                            <tr>
                                                <th>Report</th>
                                                <th>Total Calls</th>
                                                <th>Learners With Calls</th>
                                                <th>Calls Per Learner</th>
                                                {callTypeOptions.map((callType) => (
                                                    <th key={callType.typeName}>{callType.typeName}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {comparisonRows.map((row) => (
                                                <tr key={row.label}>
                                                    <td>{row.label}</td>
                                                    <td>{row.totalCalls}</td>
                                                    <td>{row.pupilsWithCalls}</td>
                                                    <td>{row.callsPerPupil.toFixed(1)}</td>
                                                    {callTypeOptions.map((callType) => (
                                                        <td key={`${row.label}-${callType.typeName}`}>
                                                            {row.callTypeCounts[callType.typeName] || 0}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
